import { inject, Injectable, InjectionToken } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom } from '@ngrx/operators';
import { AssetState, IAssetItem } from '@analysis/domain';
import type { TileSourceOptions } from 'openseadragon';
import { defer, EMPTY, from, Observable, Subject, timer } from 'rxjs';
import {
  type SampleAssetRepository,
  type AssetTileSourceProvider,
  UpdateAssetMetadataUseCase,
  GetAssetMetadataUseCase,
} from '@analysis/use-cases';
import { catchError, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '@telespot/web-core';

/**
 * Backoff schedule (ms) used when polling for sample assets whose backend
 * processing hasn't finished. `TelespotSampleAssetService.toAssetItem` flags
 * an asset as `AssetState.PROCESSING` when its Parse object doesn't yet have
 * `data.width`/`data.height` — i.e. before the upload pipeline finishes
 * computing the pyramid/thumbnail metadata. Five attempts ≈ 62s total.
 */
const PROCESSING_POLL_DELAYS_MS = [2000, 4000, 8000, 16000, 32000];

export interface AssetStoreState {
  assets: Map<string, TileSourceOptions>;
  error: string | undefined;
  metadata: Map<string, IAssetItem>;
  loading: boolean;
  selectedAsset: string | null;
}

export const ASSET_TILE_SOURCE_PROVIDER = new InjectionToken<AssetTileSourceProvider>('ASSET_TILE_SOURCE_PROVIDER');
export const UPDATE_ASSET_METADATA_USE_CASE = new InjectionToken<UpdateAssetMetadataUseCase>(
  'UPDATE_ASSET_METADATA_USE_CASE'
);
export const GET_ASSET_METADATA_USE_CASE = new InjectionToken<GetAssetMetadataUseCase>('GET_ASSET_METADATA_USE_CASE');
export const SAMPLE_ASSET_SERVICE = new InjectionToken<SampleAssetRepository>('SAMPLE_ASSET_SERVICE');

@Injectable()
export class AssetStore extends ComponentStore<AssetStoreState> {
  private readonly CACHE_LIMIT = 10;
  private readonly MAX_CONCURRENCY = 10;
  private readonly tileSource = inject<AssetTileSourceProvider>(ASSET_TILE_SOURCE_PROVIDER);
  private readonly updateAssetMetadataUseCase = inject(UPDATE_ASSET_METADATA_USE_CASE);
  private readonly getAssetMetadataUseCase = inject(GET_ASSET_METADATA_USE_CASE);
  private readonly sampleAssetApi = inject<SampleAssetRepository>(SAMPLE_ASSET_SERVICE);
  private readonly authService = inject(AuthService);

  constructor() {
    super({
      assets: new Map(),
      error: undefined,
      metadata: new Map(),
      loading: false,
      selectedAsset: null,
    });
  }

  // #region Selectors

  public readonly assets$ = this.select((state) => state.assets);
  public readonly error$ = this.select((state) => state.error);

  // Emits the assetId whose image details (DZI `/info`, tiles, ...) failed to
  // load. Lets consumers show an "image unavailable" state instead of an
  // endless spinner / a stale image.
  private readonly assetInfoErrorSubject$ = new Subject<string>();
  public readonly assetInfoError$ = this.assetInfoErrorSubject$.asObservable();
  public readonly metadata$ = this.select((state) => state.metadata);
  public readonly loading$ = this.select((state) => state.loading);
  public readonly selectedAssetId$ = this.select((state) => state.selectedAsset);

  public readonly items$ = this.select((state) => Array.from(state.metadata.values()));
  public readonly selectedAsset$ = this.select(this.selectedAssetId$, this.items$, (assetId, items) => {
    if (!assetId) return null;
    return items.find((item) => item.id === assetId);
  });

  public readonly selectedAssetUrl$ = this.select(this.selectedAsset$, (asset) => asset?.url);

  public readonly selectedIndex$ = this.select(this.selectedAssetId$, this.items$, (assetId, items) => {
    if (!assetId) return 0;
    const asset = items.find((item) => item.id === assetId);
    return asset ? asset.index : 0;
  });

  public readonly totalAssets$ = this.select(this.items$, (items) => items.length);

  public readonly canNavigateNext$ = this.select(
    this.selectedIndex$,
    this.totalAssets$,
    (selectedIndex, totalAssets) => selectedIndex < totalAssets - 1
  );

  public readonly canNavigatePrevious$ = this.select(this.selectedIndex$, (selectedIndex) => selectedIndex > 0);

  // Helper selectors
  public readonly assetIds$ = this.select((state) => Array.from(state.metadata.keys()));
  public readonly assetCount$ = this.select((state) => state.metadata.size);

  public readonly tileSource$ = this.select(this.selectedAssetId$, this.assets$, (assetId, assets) => {
    if (!assetId || !assets.has(assetId)) {
      return null;
    }
    return assets.get(assetId);
  });

  public readonly focusPoint$ = this.select(this.selectedAssetId$, this.items$, (assetId, items) => {
    const asset = items.find((item) => item.id === assetId);

    if (!asset) {
      return { x: 0.5, y: 0.5 };
    }

    const width = asset.data?.width;
    const height = asset.data?.height;

    const centerPoint = {
      x: width ? width / 2 : 0.5,
      y: height ? height / 2 : 0.5,
    };

    return centerPoint;
  });

  public readonly stitchingScans$ = this.select(this.selectedAssetId$, this.items$, (assetId, items) => {
    const asset = items.find((item) => item.id === assetId);

    if (!asset?.assetFile?.includes('scan-')) {
      return undefined;
    }

    return asset.assetFile.substring(
      asset.assetFile.indexOf('-', asset.assetFile.indexOf('-') + 1) + 1,
      asset.assetFile.lastIndexOf('.')
    );
  });

  public readonly hspan$ = this.select(this.selectedAssetId$, this.items$, (assetId, items) => {
    const asset = items.find((item) => item.id === assetId);

    if (!asset?.data) {
      return undefined;
    }

    const data = asset.data;
    const couplingMultiplier = data?.microscope?.couplingMultiplier;
    const mobjective = data?.microscopeObjective;
    const nFov = data?.microscope?.nFov;
    const zoom = (data?.zoom?.zoom as any) ?? data?.zoom;

    if (couplingMultiplier && mobjective && nFov && data?.zoom) {
      return (zoom / data?.microscope.couplingMultiplier) * (data?.microscope.nFov / data?.microscopeObjective);
    }

    return asset.hspan;
  });

  public readonly dataEditable$ = this.select(this.selectedAssetId$, this.items$, (assetId, items) => {
    if (!assetId) return false;
    const asset = items.find((item) => item.id === assetId);
    if (!asset) return false;

    return this.authService.hasRoles(['admin', 'technicianmanager', 'technician']);
  });

  // #endregion

  // #region Updaters

  private readonly addAsset = this.updater((state, action: { assetId: string; info: TileSourceOptions }) => {
    const newAssets = new Map(state.assets);

    newAssets.set(action.assetId, action.info);

    // Clearing on success matters: `error` was write-only, so one failed tile source left
    // every consumer of `error$` latched for the life of the view. Per-asset failures are
    // still reported through `assetInfoError$`.
    return {
      ...state,
      assets: newAssets,
      error: undefined,
    };
  });

  private readonly evictAssets = this.updater((state, count: number) => {
    const newAssets = new Map(state.assets);
    Array.from(newAssets.keys())
      .slice(0, count)
      .forEach((key) => newAssets.delete(key));
    return { ...state, assets: newAssets };
  });

  private readonly setError = this.updater((state, error: string | undefined) => ({ ...state, error }));

  public readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
  }));

  public readonly setSelectedAsset = this.updater((state, selectedAsset: string | null) => ({
    ...state,
    selectedAsset,
  }));

  public readonly clearAssets = this.updater((state) => ({
    ...state,
    assets: new Map(),
    metadata: new Map(),
    selectedAsset: null,
  }));

  public readonly addAssetMetadata = this.updater((state, action: { assetId: string; info: IAssetItem }) => {
    const newMetadata = new Map(state.metadata);
    newMetadata.set(action.assetId, action.info);
    return {
      ...state,
      metadata: newMetadata,
    };
  });

  public readonly updateAssetData = this.updater(
    (state, action: { assetId: string; data: { fieldPath: string; value: any } }) => {
      const { assetId, data } = action;
      const newMetadata = new Map(state.metadata);
      const existingAsset = newMetadata.get(assetId);

      if (existingAsset) {
        const clonedData = structuredClone(existingAsset.data || {});
        this.updateNestedProperty(clonedData, data.fieldPath, data.value);

        newMetadata.set(assetId, {
          ...existingAsset,
          data: clonedData,
        });
      }

      return {
        ...state,
        metadata: newMetadata,
      };
    }
  );

  public readonly replaceAssetData = this.updater((state, action: { assetId: string; data: any }) => {
    const { assetId, data } = action;
    const newMetadata = new Map(state.metadata);
    const existingAsset = newMetadata.get(assetId);

    if (existingAsset) {
      newMetadata.set(assetId, {
        ...existingAsset,
        data: structuredClone(data || {}),
      });
    }

    return {
      ...state,
      metadata: newMetadata,
    };
  });

  // #endregion

  // #region Effects

  public readonly selectAssetOnFocusedRoi = this.effect((focusedRoi$: Observable<{ assetId?: string } | undefined>) =>
    focusedRoi$.pipe(
      map((roi) => roi?.assetId),
      filter((assetId): assetId is string => !!assetId),
      tap((assetId: string) => this.selectAsset(assetId))
    )
  );

  public readonly fetchAssetInfo = this.effect((assetId$: Observable<string>) =>
    assetId$.pipe(
      concatLatestFrom(() => [this.assets$, this.metadata$]),
      mergeMap(([assetId, assets, metadata]) => {
        if (assets.has(assetId)) return EMPTY;

        const item = metadata.get(assetId);
        if (item && (!item.data?.width || !item.data?.height)) return EMPTY;

        return from(this.tileSource.getTileSource(assetId)).pipe(
          tap((info) => this.addAsset({ assetId, info })),
          catchError((error) => {
            if (assets.size + 1 > this.CACHE_LIMIT) this.evictAssets(1);
            this.setError(error.message);
            this.assetInfoErrorSubject$.next(assetId);
            return EMPTY;
          })
        );
      }, this.MAX_CONCURRENCY)
    )
  );

  public readonly selectAsset = this.effect((assetId$: Observable<string | null>) =>
    assetId$.pipe(
      concatLatestFrom(() => this.items$),
      tap(([assetId, items]) => {
        let selectedId: string | null = assetId;

        if (!assetId && items.length > 0) {
          selectedId = items[0].id;
        }
        if (!selectedId) return EMPTY;

        this.setSelectedAsset(selectedId);
        this.fetchAssetInfo(selectedId);
      }),
      catchError((error) => {
        console.error(`Failed to select asset: ${error.message}`);
        return EMPTY;
      })
    )
  );

  public readonly updateAssetDataField = this.effect((keyValue$: Observable<{ fieldPath: string; value: any }>) =>
    keyValue$.pipe(
      concatLatestFrom(() => [this.selectedAssetId$, this.items$]),
      tap(([{ fieldPath, value }, assetId, items]) => {
        if (!assetId) return;
        const asset = items.find((item) => item.id === assetId);
        if (!asset) return;

        this.updateAssetData({
          assetId,
          data: { fieldPath, value },
        });
      })
    )
  );

  public readonly loadSampleAssets = this.effect((sampleId$: Observable<string>) =>
    sampleId$.pipe(
      tap(() => this.setLoading(true)),
      // `switchMap` so a superseded sample's request is abandoned — with `mergeMap` a
      // slower earlier request could land last and leave the refstrip showing the
      // previous sample's assets.
      switchMap((sampleId) =>
        from(this.sampleAssetApi.listSampleAssets(sampleId)).pipe(
          map((sampleAssets) => ({ sampleId, sampleAssets })),
          // Caught INSIDE the switchMap: at the outer level a single failure completed
          // the whole effect, so the refstrip never loaded again for the life of the view.
          catchError((error) => {
            console.error(`Failed to load sample assets: ${error.message}`);
            this.setLoading(false);
            return EMPTY;
          })
        )
      ),
      // The writes live DOWNSTREAM of the switch, never inside the promise: a promise
      // cannot be cancelled, so writing from its body would apply a superseded sample's
      // assets even though its result was discarded.
      tap(({ sampleId, sampleAssets }) => {
        sampleAssets.forEach((assetItem) => {
          this.addAssetMetadata({
            assetId: assetItem.id,
            info: assetItem,
          });
        });

        // If any asset is still processing (no width/height yet), schedule a
        // poll that re-queries with backoff until they all resolve.
        // Without this the refstrip stays stuck on the placeholder.svg until
        // the user reloads the page, because the PROCESSING state is only
        // set at load time and never re-evaluated.
        if (sampleAssets.some((a) => a.state === AssetState.PROCESSING)) {
          this.pollProcessingAssets(sampleId);
        }

        this.setLoading(false);
      })
    )
  );

  /**
   * Re-queries `listSampleAssets` with exponential backoff while any asset
   * in this sample is still in PROCESSING state. Updates only the items
   * whose state changes (in-place via `addAssetMetadata`) so the strip's
   * selection/scroll position survives. Stops once all assets are SUCEEDED
   * or after exhausting the backoff schedule.
   *
   * `switchMap` at the source means launching a fresh poll for a new
   * sample id (e.g. user navigates to a different sample mid-poll) cancels
   * the previous polling chain — no risk of stale updates landing on the
   * wrong sample.
   */
  public readonly pollProcessingAssets = this.effect((sampleId$: Observable<string>) =>
    sampleId$.pipe(switchMap((sampleId) => this.pollSampleAssets$(sampleId, 0)))
  );

  private pollSampleAssets$(sampleId: string, attemptIndex: number): Observable<void> {
    if (attemptIndex >= PROCESSING_POLL_DELAYS_MS.length) return EMPTY;
    return defer(() =>
      timer(PROCESSING_POLL_DELAYS_MS[attemptIndex]).pipe(
        switchMap(() => from(this.sampleAssetApi.listSampleAssets(sampleId))),
        switchMap((items) => {
          let stillProcessing = false;
          for (const item of items) {
            const current = this.get((s) => s.metadata.get(item.id));
            // Only patch when state actually transitioned, otherwise we'd
            // be cloning the metadata Map (and triggering full re-renders
            // of the refstrip) for no reason.
            if (current && current.state !== item.state) {
              this.addAssetMetadata({ assetId: item.id, info: item });
            }
            if (item.state === AssetState.PROCESSING) stillProcessing = true;
          }
          if (!stillProcessing) return EMPTY;
          return this.pollSampleAssets$(sampleId, attemptIndex + 1);
        }),
        catchError((err) => {
          // Network blip — don't propagate to avoid killing the effect;
          // we'll naturally try again on the next manual reload.
          console.warn(`[asset.store] processing-poll failed for sample ${sampleId}:`, err);
          return EMPTY;
        })
      )
    );
  }

  public readonly saveAssetData = this.effect((trigger$: Observable<any>) =>
    trigger$.pipe(
      concatLatestFrom(() => [this.selectedAssetId$, this.items$]),
      mergeMap(([, assetId, items]) => {
        if (!assetId) return EMPTY;
        const asset = items.find((item) => item.id === assetId);
        if (!asset) return EMPTY;

        return from(this.updateAssetMetadataUseCase.execute({ assetId, data: asset.data })).pipe(
          catchError((error) => {
            console.error(`Failed to save asset data: ${error.message}`);
            return EMPTY;
          })
        );
      })
    )
  );

  public readonly discardDataChanges = this.effect((trigger$: Observable<any>) =>
    trigger$.pipe(
      concatLatestFrom(() => [this.selectedAssetId$]),
      mergeMap(([, assetId]) => {
        if (!assetId) return EMPTY;

        return from(this.getAssetMetadataUseCase.execute({ assetId })).pipe(
          tap((data) => {
            this.replaceAssetData({ assetId, data });
          }),
          catchError((error) => {
            console.error(`Failed to save asset data: ${error.message}`);
            return EMPTY;
          })
        );
      })
    )
  );

  // #endregion

  // #region Helpers

  private updateNestedProperty(obj: any, fieldPath: string, value: any): void {
    const keys = fieldPath.split('.');
    let current = obj;

    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i];

      if (!(k in current) || typeof current[k] !== 'object') {
        current[k] = {};
      }

      current = current[k];
    }

    current[keys[keys.length - 1]] = value;
  }

  // #endregion
}
